import { AppIcons } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';

export const COLLEGE_ICON_MAP: Record<string, AppIcons> = {
  'Agriculture & Natural Resources': 'SunSetting02',
  'Arts & Sciences (Liberal Arts)': 'BookOpen01',
  Business: 'Briefcase01',
  'Communication/Journalism/Media': 'PhoneCall01',
  Education: 'GraduationHat01',
  Engineering: 'Speedometer04',
  'Fine Arts/Design/Architecture': 'Palette',
  'Health/Nursing/Allied Health': 'MedicalSquare',
  'Public Affairs/Criminal Justice/Policy': 'Bank',
  'Theology/Divinity/Religion': 'MoonStar',
};

export const getCollegeIcon = (collegeName: string): AppIcons =>
  COLLEGE_ICON_MAP[collegeName] ?? 'GraduationHat02';

export type Major = Readonly<{
  id: number;
  name: string;
  media_url?: string;
  description: string;
  typicalCollege: string;
  demand: MajorDemand;
  optionValueScore: number;
  optionValueName: MajorOptionValue;
  underEmployment: number;
  underEmploymentRationale: string;
  competitiveness: MajorCompetitiveness;
  workload: MajorWorkloadIntensity;
  gradSchoolNecessity: MajorGradSchoolNecessity;
  gradSchoolNecessityRationale: string;
  code: string;
  careerPathRelevance: MajorCareerPathRelevance[];
  rigor: number;
  specialization: MajorSpecialization;
  category: string;
  sampleJobs: string[];
  medianWageEarlyCareer: number;
  medianWageMidCareer: number;
}>;

export type MajorResponse = Readonly<{
  id: number;
  name: string;
  media_url?: string;
  description: string;
  typical_college: string;
  demand: MajorDemand;
  option_value_score: number;
  option_value_name: MajorOptionValue;
  underemployment: number;
  underemployment_rationale: string;
  competitiveness: MajorCompetitiveness;
  workload: MajorWorkloadIntensity;
  grad_school_necessity: MajorGradSchoolNecessity;
  grad_school_necessity_rationale: string;
  code: string;
  careerPathRelevance: MajorCareerPathRelevanceResponse[];
  rigor: number;
  specialization: MajorSpecialization;
  category: string;
  sample_jobs: string[];
  median_wage_early_career: number;
  median_wage_mid_career: number;
}>;

export type MajorCareerPathRelevance = Readonly<{
  id: number;
  academicMajorsId: number;
  careerPathId: number;
  relevance: 'Low' | 'Medium' | 'High';
  rationale: string;
}>;

export type MajorCareerPathRelevanceResponse = Readonly<{
  id: number;
  academic_majors_id: number;
  career_path_id: number;
  relevance: 'Low' | 'Medium' | 'High';
  rationale: string;
}>;

export type MajorSearchResult = Readonly<{
  id: number;
  name: string;
}>;

const DEMAND_OPTIONS = ['Low', 'Moderate', 'High'];
const OPTION_VALUE_OPTIONS = ['Very Low', 'Low', 'Moderate', 'High', 'Very High'];
const RIGOR_OPTIONS = ['Not Rigorous', 'Moderate', 'Rigorous', 'Very Rigorous'];

const SPECIALIZATION_OPTIONS = [
  'Very Generalist',
  'Generalist',
  'Moderate',
  'Specialized',
  'Highly Specialized',
];

const COMPETITIVENESS_OPTIONS = ['Low', 'Moderate', 'High'];
const WORKLOAD_OPTIONS = ['Low', 'Moderate', 'High'];
const GRAD_SCHOOL_NECESSITY_OPTIONS = ['Rare', 'Sometimes', 'Frequently', 'Most of the time'];

type MajorDemand = (typeof DEMAND_OPTIONS)[number];
type MajorOptionValue = (typeof OPTION_VALUE_OPTIONS)[number];
type MajorRigor = (typeof RIGOR_OPTIONS)[number];
type MajorSpecialization = (typeof SPECIALIZATION_OPTIONS)[number];
type MajorCompetitiveness = (typeof COMPETITIVENESS_OPTIONS)[number];
type MajorWorkloadIntensity = (typeof WORKLOAD_OPTIONS)[number];
type MajorGradSchoolNecessity = (typeof GRAD_SCHOOL_NECESSITY_OPTIONS)[number];

export type MajorAttributes = Readonly<{
  demand: MajorDemand;
  optionValueName: MajorOptionValue;
  rigor: MajorRigor;
  specialization: MajorSpecialization;
  competitiveness: MajorCompetitiveness;
  workload: MajorWorkloadIntensity;
  gradSchoolNecessity: MajorGradSchoolNecessity;
}>;

export const MAJOR_ATTRIBUTE_OPTIONS = {
  demand: DEMAND_OPTIONS,
  optionValueName: OPTION_VALUE_OPTIONS,
  rigor: RIGOR_OPTIONS,
  specialization: SPECIALIZATION_OPTIONS,
  competitiveness: COMPETITIVENESS_OPTIONS,
  workload: WORKLOAD_OPTIONS,
  gradSchoolNecessity: GRAD_SCHOOL_NECESSITY_OPTIONS,
} as const;

export const MAJOR_ATTRIBUTE_DISPLAY_LABELS: Readonly<{
  [K in keyof typeof MAJOR_ATTRIBUTE_OPTIONS]: Readonly<Record<string, string>>;
}> = {
  demand: {
    Low: 'Low',
    Moderate: 'Moderate',
    High: 'High',
  },
  optionValueName: {
    'Very Low': 'Very Low',
    Low: 'Low',
    Moderate: 'Moderate',
    High: 'High',
    'Very High': 'Very High',
  },
  rigor: {
    'Not Rigorous': 'Low',
    Moderate: 'Moderate',
    Rigorous: 'Rigorous',
    'Very Rigorous': 'Very Rigorous',
  },
  specialization: {
    'Very Generalist': 'Very Low',
    Generalist: 'Low',
    Moderate: 'Moderate',
    Specialized: 'High',
    'Highly Specialized': 'Very High',
  },
  competitiveness: {
    Low: 'Low',
    Moderate: 'Moderate',
    High: 'High',
  },
  workload: {
    Low: 'Low',
    Moderate: 'Moderate',
    High: 'High',
  },
  gradSchoolNecessity: {
    Rare: 'Rare',
    Sometimes: 'Sometimes',
    Frequently: 'Frequently',
    'Most of the Time': 'Most of Time',
  },
} as const;

export type AcademicExperienceRowData = Readonly<{
  title: string;
  tooltipText: string;
  options: readonly string[];
  selectedValue: string;
  attributeKey: keyof MajorAttributes;
}>;
